import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { listClientIdentities } from '@/services/clientService';
import { ClientIdentityProjection } from '@/utils/nameMatching';
import { logger } from '@/utils/logger';
import {
    FirmClientIdentityIndexContext,
    FirmClientIdentityIndexContextType,
} from './FirmClientIdentityIndexContext.definition';

// 5-minute TTL — a coworker who creates a client in another tab will land
// in the cache within this window; the submit-time modal is the backstop
// for the narrow gap in between.
const TTL_MS = 5 * 60 * 1000;

interface ProviderProps {
    children: React.ReactNode;
}

export function FirmClientIdentityIndexProvider({ children }: ProviderProps) {
    const { effectiveFirmId } = useAuth();

    const [index, setIndex] = useState<ClientIdentityProjection[]>([]);
    const [isReady, setIsReady] = useState(false);
    const fetchedAtRef = useRef<number>(0);
    const inFlightRef = useRef<Promise<void> | null>(null);

    const load = useCallback(async (firmId: string) => {
        if (inFlightRef.current) return inFlightRef.current;

        const promise = (async () => {
            try {
                const identities = await listClientIdentities(firmId);
                setIndex(identities);
                fetchedAtRef.current = Date.now();
                setIsReady(true);
            } catch (err) {
                logger.warn('Failed to load client identity index', {
                    error: err instanceof Error ? err.message : String(err),
                });
                // Keep whatever we have; isReady stays whatever it was.
                // Submit-time backstop still catches duplicates.
            } finally {
                inFlightRef.current = null;
            }
        })();

        inFlightRef.current = promise;
        return promise;
    }, []);

    // Initial fetch + firm switch.
    useEffect(() => {
        if (!effectiveFirmId) {
            setIndex([]);
            setIsReady(false);
            fetchedAtRef.current = 0;
            return;
        }
        void load(effectiveFirmId);
    }, [effectiveFirmId, load]);

    // Periodic staleness check. The interval is coarse (TTL_MS); if a
    // consumer mounts while stale, the next tick refreshes it.
    useEffect(() => {
        if (!effectiveFirmId) return;
        const interval = setInterval(() => {
            if (Date.now() - fetchedAtRef.current > TTL_MS) {
                void load(effectiveFirmId);
            }
        }, TTL_MS);
        return () => clearInterval(interval);
    }, [effectiveFirmId, load]);

    const invalidateIdentityIndex = useCallback(() => {
        if (!effectiveFirmId) return;
        fetchedAtRef.current = 0;
        void load(effectiveFirmId);
    }, [effectiveFirmId, load]);

    const value: FirmClientIdentityIndexContextType = {
        index,
        isReady,
        invalidateIdentityIndex,
    };

    return (
        <FirmClientIdentityIndexContext.Provider value={value}>
            {children}
        </FirmClientIdentityIndexContext.Provider>
    );
}
